import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DashboardVisibleContext } from '../DashboardView/dashboard-visible';
import * as L from '../../../lib/home-map/phone-layout';
import {
  Sim,
  type BadgeFill,
  type FlowCategory,
  agentHoleRadius,
  buildPaths,
  flowCategories,
  glyphShapes,
  glyphSize,
  glyphViewBox,
  inGlyphInk,
  inGlyphStroke,
  inSparkInk,
  incomingGlyphs,
  outGlyphInk,
  outGlyphStroke,
  outLayerClipX,
  pointAt,
  polylineSlice,
  sparkAt,
  sparkLength,
  sparkSpeed,
  sparkWidth,
  tagFillInk,
  tagLook,
  tagShadowAlpha,
  tagShadowBlur,
  tagShadowRgb,
  tagShadowY,
  tagSize,
  type FlowGlyph,
  type FlowPolyline,
  type GlyphShape,
} from '../../../lib/home-map/phone-flow';
import { tokenValue } from '../../../lib/token-value';
import styles from './Home3PhoneMap.module.css';

/**
 * **The octopus's ambient flow** on mobile web — the "Symphony Mobile.html" prototype's motion, as iOS and Android
 * draw it: tags ride in from the channels tray and slide under the ring; some come back out from under an agent
 * and run along a rail wire, under their category's icon; a short spark loops along each of those wires.
 *
 * The model is `lib/home-map/phone-flow.ts` (pinned with both phones to
 * `native-apps/shared/activity-map-flow/contract.json`); this file is its frame clock and its paint. Decoration only:
 * it takes no pointer and is hidden from assistive technology.
 *
 * ## Two layers, one sim
 *
 * Two DPR-scaled `<canvas>`es the size of the wires' layer, with the board (`children`) between them, so the z-order is
 * wires → OUT → board → IN (user decision 2026-11-03, as desktop's `CanvasDemoFlow` layers it):
 *
 * - the OUT layer (every rail's spark and the outputs) is UNDER the board, so an output slides into its rail icon's
 *   art and is hidden by it. It is cut at the rail icons' centre line (`outLayerClipX`) — nothing of it shows right
 *   of an icon's centre, where the art stops covering it — and keeps a hole at each agent, so an output emerges from
 *   under its dot.
 * - the IN layer (the channels wire's spark and the incoming tags) stays OVER the board, clipped outside the ring.
 *
 * One `Sim`, one frame clock, one paint: both canvases are drawn from the same state on the same frame.
 *
 * ## It is ambient
 *
 * Nothing here touches the headline — the REAL count, which never ticks — and nothing here reflects the account's
 * activity. The rail badges FILL from it: an output reaching its rail icon adds one to that badge, up to the real
 * count, and while a badge is short the tags are aimed at the short ones; once all are full an output's wire is a
 * uniform random pick, as on desktop (`BadgeFill` / `pickTarget` in `phone-flow.ts`). Only `slugs` (every
 * channel chip, folded ones included: the channels the account has) pick the incoming glyphs.
 *
 * ## When it runs
 *
 * Only while the map is the view shown (this mounts only inside `Home3PhoneMap`, which mounts only in the map
 * view), the home is the surface on screen (`DashboardVisibleContext` — `DashboardHost` keeps a visited home
 * mounted, hidden, under every other screen), the tab is visible (`document.visibilityState`), and no card or the
 * Brief modal is open (`paused`). Otherwise no frame is scheduled: the last frame stays and the sim keeps its state,
 * resuming where it stopped (its `maxDt` clamp means a resume never jumps). Under `prefers-reduced-motion: reduce`
 * (followed live) the layer is not drawn at all — the static map is the whole screen.
 */
export function Home3PhoneMapFlow({
  scale,
  width,
  height,
  agents,
  slugs,
  paused,
  fill,
  onFill,
  onLand,
  children,
}: {
  /** The board's own scale (`scaleTo`) — every path is built from it, in screen points. */
  scale: L.Scale;
  /** The layer's size in screen pixels — the wires' layer's. */
  width: number;
  height: number;
  /** How many agent dots the ring shows. */
  agents: number;
  /** Every channel chip's slug, in tray order. */
  slugs: readonly string[];
  /** A card or the Brief modal is open over the map. */
  paused: boolean;
  /** The rail badges' fill (`BadgeFill`, the map's, one per appearance): the flow aims its tags at it and fills it. */
  fill: BadgeFill;
  /** The fill moved on this frame — an output reached its rail icon — so the map redraws its badges. */
  onFill: () => void;
  /** Outputs reached these categories' rail icons on this frame (`Sim.justLanded`): the map pulses them. */
  onLand?: (landed: readonly FlowCategory[]) => void;
  /** The board — drawn between the out layer (under it) and the in layer (over it). */
  children?: ReactNode;
}) {
  const onScreen = useContext(DashboardVisibleContext);
  const pageVisible = usePageVisible();
  const reduced = useReducedMotion();
  const running = onScreen && pageVisible && !paused && !reduced && width > 0 && height > 0;

  // The geometry: paths are rebuilt, and the sim with them, only when the layout really changes (a resize or the
  // agent count) — not on every render, which hands a fresh `Scale` each time.
  const { sx, sy, originY } = scale;
  const geometry = useMemo(() => {
    const s = new L.Scale(sx, sy, originY);
    return { scale: s, paths: buildPaths(s, agents) };
  }, [sx, sy, originY, agents]);

  const glyphKey = slugs.join('\u0000');
  const glyphs = useMemo(() => incomingGlyphs(glyphKey ? glyphKey.split('\u0000') : []), [glyphKey]);

  // The chips are read through a ref, so a new chip reaches the running sim without resetting it.
  const glyphsRef = useRef(glyphs);
  glyphsRef.current = glyphs;
  // The fill outlives a geometry change (it is the map's), so a resize rebuilds the sim but not the badges.
  const sim = useMemo(() => new Sim(geometry.paths, glyphsRef.current, geometry.scale, Math.random, fill), [geometry, fill]);
  const onFillRef = useRef(onFill);
  onFillRef.current = onFill;
  const onLandRef = useRef(onLand);
  onLandRef.current = onLand;
  useEffect(() => sim.setGlyphs(glyphs), [sim, glyphs]);

  const outRef = useRef<HTMLCanvasElement | null>(null);
  const inRef = useRef<HTMLCanvasElement | null>(null);
  const inksRef = useRef<FlowInks | null>(null);
  const dprRef = useRef(1);

  const paint = useCallback(
    (target: Sim) => {
      const inks = inksRef.current;
      const outCtx = outRef.current?.getContext('2d');
      const inCtx = inRef.current?.getContext('2d');
      if (!inks || !outCtx || !inCtx) return;
      drawOut(outCtx, target, inks, dprRef.current, width, height);
      drawIn(inCtx, target, inks, dprRef.current, width, height);
    },
    [width, height],
  );

  // Size the backing store to the device's pixels (setting it clears it), read the inks, and draw the current state.
  useLayoutEffect(() => {
    const canvases = [outRef.current, inRef.current];
    if (!canvases[0] || !canvases[1]) return;
    const dpr = window.devicePixelRatio || 1;
    dprRef.current = dpr;
    for (const canvas of canvases) {
      if (!canvas) continue;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
    }
    inksRef.current = readInks(canvases[0]);
    paint(sim);
  }, [width, height, reduced, sim, paint]);

  // The inks are tokens resolved to strings (a canvas cannot read `var()`): re-read them when the theme flips.
  useEffect(() => {
    if (reduced || typeof MutationObserver === 'undefined') return undefined;
    const reread = () => {
      if (!outRef.current) return;
      inksRef.current = readInks(outRef.current);
      paint(sim);
    };
    const mo = new MutationObserver(reread);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-brand'] });
    return () => mo.disconnect();
  }, [reduced, sim, paint]);

  // The frame clock. dt is real frame time; the sim clamps it.
  useEffect(() => {
    if (!running) return undefined;
    let frame = 0;
    let last: number | null = null;
    const tick = (now: number) => {
      if (last !== null) {
        const before = fill.version;
        sim.step((now - last) / 1000);
        if (fill.version !== before) onFillRef.current();
        if (sim.justLanded.length > 0) onLandRef.current?.(sim.justLanded);
      }
      last = now;
      paint(sim);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [running, sim, paint, fill]);

  // Under reduced motion no flow is drawn: the board alone.
  if (reduced) return <>{children}</>;
  return (
    <>
      <canvas ref={outRef} className={styles.flow} style={{ width, height }} aria-hidden data-testid="home3-phone-map-flow-out" />
      {children}
      <canvas ref={inRef} className={styles.flow} style={{ width, height }} aria-hidden data-testid="home3-phone-map-flow" />
    </>
  );
}

/** The inks the paint needs, resolved from their tokens on the canvas itself. */
interface FlowInks {
  tagFill: string;
  inGlyph: string;
  outGlyph: string;
  inSpark: string;
  shadow: string;
  /** Per rail row, its wire's ink: a rail's spark and its outputs' disc. */
  rail: string[];
}

function readInks(el: Element): FlowInks {
  return {
    tagFill: tokenValue(tagFillInk, 'transparent', el),
    inGlyph: tokenValue(inGlyphInk, 'transparent', el),
    outGlyph: tokenValue(outGlyphInk, 'transparent', el),
    inSpark: tokenValue(inSparkInk, 'transparent', el),
    shadow: `rgba(${tokenValue(tagShadowRgb, '0, 0, 0', el)}, ${tagShadowAlpha})`,
    rail: L.rails.map((rail) => tokenValue(L.railWireInk[rail], 'transparent', el)),
  };
}

function begin(ctx: CanvasRenderingContext2D, dpr: number, width: number, height: number) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
}

/** The IN layer, over the board: the channels wire's spark and the incoming tags, clipped OUTSIDE the ring (even-odd:
 *  board minus circle), so a tag slides under it. */
function drawIn(ctx: CanvasRenderingContext2D, sim: Sim, inks: FlowInks, dpr: number, width: number, height: number) {
  const { paths } = sim;
  begin(ctx, dpr, width, height);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  circle(ctx, paths.ringCentre.x, paths.ringCentre.y, paths.ringRadius);
  ctx.clip('evenodd');
  drawSpark(ctx, paths.inWire, sim.t, sim.sparkDelays[0], inks.inSpark, sim);
  for (const tag of sim.ins) {
    const at = pointAt(paths.in.points, tag.p * paths.in.length);
    const look = tagLook(tag.p);
    drawTag(ctx, at.x, at.y, look.opacity, look.scale, inks.tagFill, inks.inGlyph, tag.glyph, inGlyphStroke, inks.shadow, sim, dpr);
  }
  ctx.restore();
}

/** The OUT layer, under the board: every rail's spark (the Brief's included) and the outputs, cut at the rail icons'
 *  centre line (the art hides the rest) with a hole at each agent. No hole at a rail icon: the icon covers the tag. */
function drawOut(ctx: CanvasRenderingContext2D, sim: Sim, inks: FlowInks, dpr: number, width: number, height: number) {
  const { paths, scale } = sim;
  begin(ctx, dpr, width, height);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, Math.min(width, outLayerClipX(paths)), height);
  const agentHole = agentHoleRadius(scale);
  for (const p of paths.agentCentres) circle(ctx, p.x, p.y, agentHole);
  ctx.clip('evenodd');
  paths.outWires.forEach((wire, i) => drawSpark(ctx, wire, sim.t, sim.sparkDelays[1 + i], inks.rail[i], sim));
  for (const out of sim.outs) {
    const path = paths.out[out.rail];
    const at = pointAt(path.points, out.p * path.length);
    const glyph = flowCategories[out.rail - 1];
    drawTag(ctx, at.x, at.y, 1, 1, inks.rail[out.rail], inks.outGlyph, glyph, outGlyphStroke, inks.shadow, sim, dpr);
  }
  ctx.restore();
}

function circle(ctx: CanvasRenderingContext2D | Path2D, x: number, y: number, r: number) {
  ctx.moveTo(x + r, y);
  ctx.arc(x, y, r, 0, Math.PI * 2);
}

/** A wire's spark: its own stretch from `head − sparkLength` to `head`, in the wire's ink, round-capped. */
function drawSpark(ctx: CanvasRenderingContext2D, wire: FlowPolyline, t: number, delay: number, ink: string, sim: Sim) {
  const { scale } = sim;
  const spark = sparkAt(t, wire.length, delay, scale.size(sparkSpeed));
  if (!spark.visible || spark.opacity <= 0) return;
  const dash = polylineSlice(wire.points, Math.max(0, spark.head - scale.size(sparkLength)), spark.head);
  if (dash.length < 2) return;
  ctx.globalAlpha = spark.opacity;
  ctx.strokeStyle = ink;
  ctx.lineWidth = scale.size(sparkWidth);
  ctx.beginPath();
  ctx.moveTo(dash[0].x, dash[0].y);
  for (let i = 1; i < dash.length; i += 1) ctx.lineTo(dash[i].x, dash[i].y);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/** A tag: a disc with a soft shadow and a stroked glyph centred in it, at `look` opacity and scale. */
function drawTag(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  opacity: number,
  look: number,
  fill: string,
  ink: string,
  glyph: FlowGlyph,
  stroke: number,
  shadow: string,
  sim: Sim,
  dpr: number,
) {
  if (opacity <= 0) return;
  const { scale } = sim;
  ctx.globalAlpha = opacity;
  ctx.save();
  // A canvas shadow ignores the transform, so it is given in device pixels.
  ctx.shadowColor = shadow;
  ctx.shadowOffsetY = scale.size(tagShadowY) * dpr;
  ctx.shadowBlur = scale.size(tagShadowBlur) * dpr;
  ctx.fillStyle = fill;
  ctx.beginPath();
  circle(ctx, x, y, (scale.size(tagSize) * look) / 2);
  ctx.fill();
  ctx.restore();

  // The glyph's 16-unit viewBox drawn into its box; the stroke is set in viewBox units so it lands at `stroke`
  // frame units on screen, scaled with the tag.
  const box = scale.size(glyphSize) * look;
  const k = box / glyphViewBox;
  ctx.save();
  ctx.translate(x - box / 2, y - box / 2);
  ctx.scale(k, k);
  ctx.strokeStyle = ink;
  ctx.lineWidth = (stroke * glyphViewBox) / glyphSize;
  for (const p of glyphPaths(glyph)) ctx.stroke(p);
  ctx.restore();
  ctx.globalAlpha = 1;
}

/** Each glyph's shapes as `Path2D`s, built once on first paint (never during SSR). */
const glyphCache = new Map<FlowGlyph, Path2D[]>();
function glyphPaths(glyph: FlowGlyph): Path2D[] {
  let cached = glyphCache.get(glyph);
  if (!cached) {
    cached = glyphShapes[glyph].map(shapePath);
    glyphCache.set(glyph, cached);
  }
  return cached;
}

function shapePath(shape: GlyphShape): Path2D {
  if (shape.kind === 'path') return new Path2D(shape.d);
  const p = new Path2D();
  if (shape.kind === 'circle') {
    circle(p, shape.cx, shape.cy, shape.r);
    return p;
  }
  const { x, y, w, h, rx } = shape;
  p.moveTo(x + rx, y);
  p.lineTo(x + w - rx, y);
  p.arcTo(x + w, y, x + w, y + rx, rx);
  p.lineTo(x + w, y + h - rx);
  p.arcTo(x + w, y + h, x + w - rx, y + h, rx);
  p.lineTo(x + rx, y + h);
  p.arcTo(x, y + h, x, y + h - rx, rx);
  p.lineTo(x, y + rx);
  p.arcTo(x, y, x + rx, y, rx);
  p.closePath();
  return p;
}

/** Whether the page is visible (`document.visibilityState`). A hidden tab gets no animation frames anyway; reading
 *  it makes "the app is in the foreground" this layer's own rule rather than a browser throttling detail. */
function usePageVisible(): boolean {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const update = () => setVisible(document.visibilityState === 'visible');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  return visible;
}

/** `prefers-reduced-motion: reduce`, followed live — the reader can flip it with the map open. The map reads it too:
 *  with no flow drawn its badges show the real counts at once. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-reduced-motion: reduce)') : undefined;
    if (!mq) return undefined;
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}
